import {
  Card,
  CardHeader,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@design-system';
import { CheckCircle, XCircle, Eye } from 'lucide-react';
import { insuranceCarrierDefs } from '@shared/insuranceCarrierDefs';
import { useState } from 'react';

interface PuiHistorySectionProps {
  puiHistory: PUIHistoryItem[];
}

export function PuiHistorySection({ puiHistory }: PuiHistorySectionProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  if (!puiHistory || puiHistory.length === 0) {
    return null;
  }

  const getCarrierLogo = (fundingPolicyType: FundingPolicyType) => {
    const carrier = Object.values(insuranceCarrierDefs).find(carrierDef =>
      carrierDef.policyTypes.includes(fundingPolicyType),
    );
    return carrier?.image;
  };

  const handleViewEmail = (templateHTML: string) => {
    setSelectedTemplate(templateHTML);
  };

  const handleCloseDialog = () => {
    setSelectedTemplate(null);
  };

  return (
    <>
      <div className="space-y-3">
        <h4 className="text-foreground text-sm font-semibold">PUI Sent History</h4>
        {puiHistory.map((pui, index) => {
          const carrierLogo = getCarrierLogo(pui.fundingPolicyType);

          return (
            <Card
              key={index}
              className="bg-card border-border p-3 shadow-sm"
            >
              <div className="flex gap-3">
                {carrierLogo && (
                  <div className="w-28 shrink-0 pt-1">
                    <img
                      src={carrierLogo.src}
                      alt={pui.productName}
                      className="max-h-10 w-full object-contain object-left"
                    />
                  </div>
                )}

                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex min-w-0 items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-foreground break-words text-sm font-semibold leading-snug">
                        {pui.productName}
                      </p>

                      <div className="mt-1 flex items-center gap-1.5">
                        {pui.sentSuccessfully ? (
                          <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 shrink-0 text-destructive" />
                        )}

                        <Badge
                          variant="default"
                          className="h-5 max-w-[130px] truncate px-2 text-[11px]"
                        >
                          {pui.isJoint ? 'Joint Inquiry' : 'Individual Inquiry'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="text-muted-foreground space-y-0.5 text-xs leading-relaxed">
                    <p className="break-words">
                      <span className="text-foreground font-medium">ID:</span>{' '}
                      {pui.anonymousIdentifier}
                    </p>

                    <p>
                      <span className="text-foreground font-medium">Sent:</span>{' '}
                      {new Date(pui.sentDateTime).toLocaleString()}
                    </p>
                  </div>

                  {pui.sentSuccessfully && pui.templateHTML && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewEmail(pui.templateHTML!)}
                      className="h-8 text-xs"
                    >
                      <Eye className="mr-1.5 h-3.5 w-3.5" />
                      View Email Content
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );

          // return (
          //   <Card
          //     key={index}
          //     className="border-gray-300 bg-gray-50 p-4 shadow-sm"
          //   >
          //     <div className="flex items-start gap-3">
          //       {/* Carrier Logo */}
          //       {carrierLogo && (
          //         <div className="flex-shrink-0">
          //           <img
          //             src={carrierLogo.src}
          //             alt={pui.productName}
          //             className={`object-contain ${carrierLogo.size}`}
          //           />
          //         </div>
          //       )}

          //       {/* Content */}
          //       <div className="flex-1 space-y-2">
          //         {/* Header with product name and status */}
          //         <div className="flex items-center justify-between">
          //           <div className="flex items-center gap-2">
          //             <p className="font-semibold text-gray-900">{pui.productName}</p>
          //             {pui.sentSuccessfully ? (
          //               <CheckCircle className="h-5 w-5 text-green-600" />
          //             ) : (
          //               <XCircle className="h-5 w-5 text-red-600" />
          //             )}
          //           </div>
          //           <Badge
          //             variant={pui.isJoint ? 'default' : 'secondary'}
          //             className="text-xs"
          //           >
          //             {pui.isJoint ? 'Joint Inquiry' : 'Individual Inquiry'}
          //           </Badge>
          //         </div>

          //         {/* Details */}
          //         <div className="space-y-1 text-sm text-gray-700">
          //           <p>
          //             <span className="font-medium">ID:</span> {pui.anonymousIdentifier}
          //           </p>
          //           <p>
          //             <span className="font-medium">Sent:</span>{' '}
          //             {new Date(pui.sentDateTime).toLocaleString()}
          //           </p>
          //         </div>

          //         {/* View Email Button */}
          //         {pui.sentSuccessfully && pui.templateHTML && (
          //           <div className="pt-2">
          //             <Button
          //               variant="outline"
          //               size="sm"
          //               onClick={() => handleViewEmail(pui.templateHTML!)}
          //               className="text-xs"
          //             >
          //               <Eye className="mr-1 h-3 w-3" />
          //               View Email Content
          //             </Button>
          //           </div>
          //         )}
          //       </div>
          //     </div>
          //   </Card>
          // );
        })}
      </div>

      {/* Email Preview Dialog */}
      <Dialog
        open={!!selectedTemplate}
        onOpenChange={open => !open && handleCloseDialog()}
      >
        <DialogContent className="max-h-[90vh] w-[80vw] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Email Content Preview</DialogTitle>
            </div>
          </DialogHeader>
          <div className="mt-4">
            {selectedTemplate && (
              <div
                className="rounded-lg border bg-white p-4"
                dangerouslySetInnerHTML={{ __html: selectedTemplate }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
